// Song programs: what each strand does to the music. A program is a little
// mixing board: one channel per strand (or "*" for every strand without its
// own channel), each with an effect, what in the music drives it (the beat,
// the melody, the volume, bass or treble), colors, speed, intensity and a
// brightness fader. Households save one per song plus a default; the
// organizer's street program runs the "Admin control" houses.
//
// Rendering is pure: given the program, the music at this moment (from the
// song's analysis) and the time, every Pi computes the same frames.

import { renderShow, type Frame, type RenderStyle } from "./effects.ts";
import { EFFECTS, LIMITS, type EffectName, type Rate, type Rgb, type Show } from "./protocol.ts";

// "glow" = a steady color (no beat pattern), useful with melody colors.
export const PROGRAM_EFFECTS = ["glow", ...EFFECTS] as const;
export type ProgramEffect = (typeof PROGRAM_EFFECTS)[number];

export const DRIVES = ["beat", "melody", "volume", "bass", "treble", "steady"] as const;
export type Drive = (typeof DRIVES)[number];

export interface Channel {
  // Govee device id of the strand, or "*" for all strands without their own.
  strand: string;
  effect: ProgramEffect;
  drive: Drive;
  colors: Rgb[];
  // The note being played picks the color (the 12 notes around the color wheel).
  noteColors: boolean;
  // Effect speed against the beat: 1 = every beat, 0.5 = every other beat,
  // 2 = twice a beat; anything from 0 (still) to MAX_RATE.
  rate: number;
  // 0-100: how strongly the drive moves the brightness.
  intensity: number;
  // 0-100: how dark the unlit parts get (default DEFAULT_CONTRAST, the
  // original look). Missing in programs saved before it existed.
  contrast?: number;
  // 1-100: the channel's fader.
  brightness: number;
  mute: boolean;
}

// A tag on a song's timeline: from `at` seconds until the next tag, these
// channels play instead of the program's own. Only song programs have tags.
export interface Cue {
  at: number;
  channels: Channel[];
}

export interface Program {
  version: 1;
  // From the start of the song until the first tag (the whole song if none).
  channels: Channel[];
  // In time order.
  cues?: Cue[];
}

// Fastest effect speed: three times a beat.
export const MAX_RATE = 3;

export const MAX_CUES = 24;
// Programs travel to the Pis inside one message (LIMITS.maxMessageBytes).
export const MAX_PROGRAM_BYTES = 12 * 1024;

// What the music is doing at one moment, from the song's analysis. Levels are
// 0-1 and already smoothed (quick rise, ~0.3 s fall) so lights don't flicker.
export interface MusicMoment {
  // Seconds from the start of the song (picks the tag in effect).
  time: number;
  beatIndex: number;
  // 0-1 through the current beat.
  beatPhase: number;
  // Length of the current beat in seconds.
  beatSeconds: number;
  loudness: number;
  bass: number;
  treble: number;
  // Dominant note 0-11 (C=0 ... B=11), or null when there's no clear note.
  pitchClass: number | null;
  pitchConfidence: number;
}

export const ALL_STRANDS = "*";

// Color wheel for notes: C red, moving through orange, yellow, green, blue,
// purple and back, so melodies that climb shift hue smoothly.
export function noteColor(pitchClass: number): Rgb {
  const hue = ((pitchClass % 12) + 12) % 12 / 12;
  return hsvToRgb(hue, 1, 1);
}

function hsvToRgb(h: number, s: number, v: number): Rgb {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  const [r, g, b] = [
    [v, t, p],
    [q, v, p],
    [p, v, t],
    [p, q, v],
    [t, p, v],
    [v, p, q],
  ][i % 6]!;
  return [Math.round(r! * 255), Math.round(g! * 255), Math.round(b! * 255)];
}

// --- presets ---

export const RED_AND_GREEN: Rgb[] = [
  [255, 0, 0],
  [0, 200, 40],
];

export function channel(fields: Partial<Channel> = {}): Channel {
  return {
    strand: ALL_STRANDS,
    effect: "step",
    drive: "beat",
    colors: RED_AND_GREEN.map((c) => [...c] as Rgb),
    noteColors: false,
    rate: 1,
    intensity: 60,
    brightness: 100,
    mute: false,
    ...fields,
  };
}

// "Auto: beat": a new color on every beat, brighter when the music is louder.
export function autoBeat(colors?: Rgb[]): Program {
  return { version: 1, channels: [channel({ effect: "step", drive: "volume", ...(colors ? { colors } : {}) })] };
}

// "Auto: melody": the color follows the notes, the brightness the volume.
export function autoMelody(colors?: Rgb[]): Program {
  return {
    version: 1,
    channels: [channel({ effect: "glow", drive: "volume", noteColors: true, intensity: 70, ...(colors ? { colors } : {}) })],
  };
}

// --- rendering ---

export interface StrandContext {
  id: string;
  style: RenderStyle;
}

// One frame per strand for this moment of the song.
export function renderProgram(
  program: Program,
  music: MusicMoment,
  atMs: number,
  position: number,
  strands: StrandContext[],
): Map<string, Frame> {
  const frames = new Map<string, Frame>();
  const channels = channelsAt(program, music.time);
  const fallback = channels.find((c) => c.strand === ALL_STRANDS);
  for (const strand of strands) {
    const ch = channels.find((c) => c.strand === strand.id) ?? fallback;
    frames.set(strand.id, ch ? renderChannel(ch, music, atMs, position, strand.style) : off());
  }
  return frames;
}

// The channels in effect `t` seconds into the song: the last tag at or before
// t, else the program's own.
export function channelsAt(program: Program, t: number): Channel[] {
  let channels = program.channels;
  for (const cue of program.cues ?? []) {
    if (cue.at > t) break;
    channels = cue.channels;
  }
  return channels;
}

function off(): Frame {
  return { on: false, rgb: [0, 0, 0], brightness: 0 };
}

export function renderChannel(ch: Channel, music: MusicMoment, atMs: number, position: number, style: RenderStyle): Frame {
  if (ch.mute) return off();
  const noteKnown = music.pitchClass !== null && music.pitchConfidence >= 0.3;
  const colors = ch.noteColors && noteKnown ? [noteColor(music.pitchClass!)] : ch.colors;

  let frame: Frame;
  if (ch.effect === "glow") {
    frame = { on: true, rgb: colors[0] ?? [255, 255, 255], brightness: ch.brightness };
  } else {
    // Describe the song's real current beat as a steady beat show, so the
    // effect lands on the actual beats even where the tempo drifts.
    const beatMs = Math.max(1, music.beatSeconds * 1000);
    const bpm = Math.min(Math.max(60000 / beatMs, LIMITS.minBpm), 600);
    const show: Show = {
      kind: "effect",
      effect: ch.effect as EffectName,
      // Shows sent to the Pis are limited to RATES; a program's own rendering
      // takes any speed.
      rate: ch.rate as Rate,
      bpm,
      palette: colors.length ? colors : [[255, 255, 255]],
      brightness: ch.brightness,
      startEpochMs: atMs - (music.beatIndex + music.beatPhase) * beatMs,
      t: 0,
    };
    frame = renderShow(show, atMs, position, { ...style, contrast: ch.contrast });
  }
  if (!frame.on) return frame;

  // The drive moves the brightness: at full intensity, from dark (quiet) to
  // the fader level (loud). Steps of 5% keep the lights from chattering.
  const level = driveLevel(ch.drive, music);
  const factor = 1 - (ch.intensity / 100) * (1 - level);
  const brightness = Math.max(1, Math.round((frame.brightness * factor) / 5) * 5);
  return { ...frame, brightness: Math.min(100, brightness) };
}

function driveLevel(drive: Drive, m: MusicMoment): number {
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  switch (drive) {
    case "volume":
      return clamp(m.loudness);
    case "bass":
      return clamp(m.bass);
    case "treble":
      return clamp(m.treble);
    case "melody":
      // A clear tune glows brightest; between phrases it dims.
      return clamp(m.pitchConfidence * 0.6 + m.loudness * 0.4);
    case "beat":
    case "steady":
      return 1;
  }
}

// --- validation (programs arrive from browsers) ---

export class ProgramError extends Error {}

export function parseProgram(value: unknown): Program {
  const fail = (message: string): never => {
    throw new ProgramError(message);
  };
  const v = value as Record<string, unknown> | null;
  if (typeof v !== "object" || v === null || v.version !== 1 || !Array.isArray(v.channels)) {
    fail("a program needs version 1 and a list of channels");
  }
  const channels = parseChannels(v!.channels as unknown[], "", fail);
  if (v!.cues === undefined) return { version: 1, channels };

  if (!Array.isArray(v!.cues) || v!.cues.length > MAX_CUES) fail(`a program has at most ${MAX_CUES} tags`);
  const cues = (v!.cues as unknown[]).map((c, i): Cue => {
    const where = `tag ${i + 1}`;
    const o = c as Record<string, unknown> | null;
    if (typeof o !== "object" || o === null) fail(`${where} must be an object`);
    const at = o!.at;
    if (typeof at !== "number" || !Number.isFinite(at) || at <= 0 || at > 4 * 3600) fail(`${where}: at must be seconds into the song`);
    if (!Array.isArray(o!.channels)) fail(`${where} needs a list of channels`);
    return { at: at as number, channels: parseChannels(o!.channels as unknown[], `${where}, `, fail) };
  });
  if (cues.some((c, i) => i > 0 && c.at <= cues[i - 1]!.at)) fail("tags must be in time order, at different times");
  const program: Program = cues.length ? { version: 1, channels, cues } : { version: 1, channels };
  if (JSON.stringify(program).length > MAX_PROGRAM_BYTES) fail("this program is too big: remove some tags or strips");
  return program;
}

function parseChannels(raw: unknown[], prefix: string, fail: (message: string) => never): Channel[] {
  if (raw.length < 1 || raw.length > LIMITS.maxDevices + 1) fail(`${prefix}a program has 1 to ${LIMITS.maxDevices + 1} channels`);
  const seen = new Set<string>();
  return raw.map((c, i): Channel => {
    const ch = c as Record<string, unknown> | null;
    const where = `${prefix}channel ${i + 1}`;
    if (typeof ch !== "object" || ch === null) fail(`${where} must be an object`);
    const o = ch!;
    const strand = o.strand;
    if (typeof strand !== "string" || strand.length < 1 || strand.length > 64) fail(`${where}: strand must be a device id or "*"`);
    if (seen.has(strand as string)) fail(`${where}: strand ${strand} appears twice`);
    seen.add(strand as string);
    if (!PROGRAM_EFFECTS.includes(o.effect as ProgramEffect)) fail(`${where}: unknown effect`);
    if (!DRIVES.includes(o.drive as Drive)) fail(`${where}: unknown drive`);
    if (typeof o.rate !== "number" || !Number.isFinite(o.rate) || o.rate < 0 || o.rate > MAX_RATE) {
      fail(`${where}: rate must be 0 to ${MAX_RATE}`);
    }
    const colors = o.colors;
    if (!Array.isArray(colors) || colors.length < 1 || colors.length > LIMITS.maxPaletteColors) {
      fail(`${where}: 1 to ${LIMITS.maxPaletteColors} colors`);
    }
    const rgbs = (colors as unknown[]).map((c2) => {
      if (!Array.isArray(c2) || c2.length !== 3 || !c2.every((x) => Number.isInteger(x) && x >= 0 && x <= 255)) {
        fail(`${where}: colors must be [r, g, b] with 0-255`);
      }
      return c2 as Rgb;
    });
    const int = (x: unknown, min: number, max: number, name: string) => {
      if (!Number.isInteger(x) || (x as number) < min || (x as number) > max) fail(`${where}: ${name} must be ${min}-${max}`);
      return x as number;
    };
    if (typeof o.noteColors !== "boolean" || typeof o.mute !== "boolean") fail(`${where}: noteColors and mute must be true or false`);
    return {
      strand: strand as string,
      effect: o.effect as ProgramEffect,
      drive: o.drive as Drive,
      colors: rgbs,
      noteColors: o.noteColors as boolean,
      rate: Math.round((o.rate as number) * 100) / 100,
      intensity: int(o.intensity, 0, 100, "intensity"),
      ...(o.contrast === undefined ? {} : { contrast: int(o.contrast, 0, 100, "contrast") }),
      brightness: int(o.brightness, 1, 100, "brightness"),
      mute: o.mute as boolean,
    };
  });
}
